import { OptionList } from '@/components/ui/multi-select/OptionList'
import { SelectedTag } from '@/components/ui/multi-select/SelectedTag'
import { useMultiSelect, type Option } from '@/components/ui/multi-select/useMultiSelect'

interface MultiSelectProps {
  value: string[]
  onChange: (value: string[]) => void
  options: Option[]
  placeholder?: string
  className?: string
  /** Accessible name for the combobox. Falls back to `placeholder`. */
  ariaLabel?: string
}

export function MultiSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  className,
  ariaLabel,
}: MultiSelectProps) {
  const select = useMultiSelect(value, onChange, options)
  const { open, activeIndex } = select

  return (
    <div ref={select.containerRef} className={`relative ${className ?? ''}`}>
      {/* Input area — a <label> so clicking anywhere focuses the input (which opens the list via onFocus) */}
      <label className="input flex flex-wrap gap-1 min-h-[2.25rem] cursor-text items-center py-1">
        {select.selectedLabels.map((tag) => (
          <SelectedTag key={tag.value} label={tag.label} onRemove={() => select.removeTag(tag.value)} />
        ))}
        <input
          ref={select.inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? select.listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open && activeIndex >= 0 ? select.optionId(activeIndex) : undefined}
          aria-label={ariaLabel ?? placeholder}
          value={select.search}
          onChange={(e) => {
            select.setSearch(e.target.value)
            select.setOpen(true)
          }}
          onFocus={() => select.setOpen(true)}
          onKeyDown={select.handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[4rem] bg-transparent rounded outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 text-sm text-primary placeholder:text-muted"
        />
      </label>

      {/* Dropdown */}
      {open && (
        <OptionList
          id={select.listboxId}
          label={ariaLabel ?? placeholder}
          options={select.filtered}
          selectedValues={value}
          activeIndex={activeIndex}
          optionId={select.optionId}
          onToggle={select.toggle}
          onHover={select.setActiveIndex}
        />
      )}
    </div>
  )
}
